/**
 * 券「使用」跳转：按 applicableScenes + sceneRuntime.couponUrl，逻辑对齐活动任务 jumpUrls（RewardHub）
 */

import { Platform, Bridge, Util } from "@xt/xtbase";
import {
  COUPON_SCENE_AI_COPYTRADE,
  canShowVoucherCashRedeem,
  isFixedAmountCoupon,
  isPercentOffCoupon,
  parseUserCouponApplicableScenes,
} from "./normalizeUserCoupon";
import { couponUrlHasJumpTarget, resolveVoucherSceneCouponUrl } from "./voucherRules";
import { getCouponNavFallbackPath } from "./couponNavigateFallbacks";
import { resolveAicopyDetailPathForViewport } from "@/components/Pages/aicopy/utils/aicopyDetailRoutes";

const { getUrl } = Util;

function isPwaStandalone() {
  if (typeof window === "undefined") return false;
  try {
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
    if (window.navigator && window.navigator.standalone) return true;
  } catch {
    /* ignore */
  }
  return false;
}

export function shouldUseAppRouteForVoucherJump() {
  return Platform.isInApp() && !isPwaStandalone();
}

/** 与 RewardHub resolveTaskNativeJumpPath 一致 */
export function resolveCouponUrlNativePath(couponUrl) {
  if (!couponUrl || typeof couponUrl !== "object") return "";
  const ios = typeof couponUrl.ios === "string" ? couponUrl.ios.trim() : "";
  const android = typeof couponUrl.android === "string" ? couponUrl.android.trim() : "";
  if (Platform.isIOS()) return ios || android;
  if (Platform.isAndroid()) return android || ios;
  return android || ios;
}

/** App 未注册的旧 H5 路径（/cfd/* 已迁移至 /futures/*） */
const LEGACY_INVALID_APP_CFD_PATH = /^\/cfd\/(?:history|record)(?:\/|$|\?)/i;

/** Web 持仓历史页 → App 原生页（与 DISCOUNT 的 contract ↔ usd-m 映射一致） */
function mapFuturesRecordWebPathToAppPath(pathname) {
  const match = String(pathname || "").match(/^\/futures\/record\/([^/?#]+)/i);
  if (!match) return "";
  const symbol = decodeURIComponent(match[1]).trim().toLowerCase();
  if (!symbol || symbol === "all") return "";
  return `/futures/usd-m/history?market=${encodeURIComponent(symbol)}`;
}

/**
 * App 内券跳转 path：修正接口/兜底里的 legacy `/cfd/history`，并按券类型回退
 * @param {string} rawPath
 * @param {string|null|undefined} fallbackKind
 */
export function resolveVoucherAppRoutePath(rawPath, fallbackKind) {
  const normalized = normalizeAppRoutePath(rawPath);
  if (normalized && !LEGACY_INVALID_APP_CFD_PATH.test(normalized)) {
    const fromRecord = mapFuturesRecordWebPathToAppPath(normalized);
    if (fromRecord) return fromRecord;
    if (!/^\/futures\/record\//i.test(normalized)) return normalized;
  }
  return normalizeAppRoutePath(getCouponNavFallbackPath(fallbackKind, { preferApp: true }));
}

/** 与 RewardHub normalizeAppRoutePath 一致 */
export function normalizeAppRoutePath(raw) {
  const s = (raw || "").trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) {
    try {
      const u = new URL(s);
      return `${u.pathname}${u.search}${u.hash}`;
    } catch {
      return s;
    }
  }
  if (!s.startsWith("/")) return `/${s.replace(/^\/+/, "")}`;
  return s;
}

/** Web/H5 跳转：仅认 web/h5/mobile/common，不把 ios/android 当作 H5 路径 */
export function resolveCouponUrlWebPath(couponUrl) {
  if (!couponUrl) return "";
  if (typeof couponUrl === "string" && couponUrl.trim()) return couponUrl.trim();
  if (typeof couponUrl !== "object") return "";
  const preferKeys = ["web", "WEB", "Web", "h5", "H5", "mobile", "MOBILE", "common", "COMMON"];
  for (const k of preferKeys) {
    const v = couponUrl[k];
    if (typeof v === "string" && v.trim()) return v.trim();
    if (v && typeof v === "object" && typeof v.url === "string" && v.url.trim()) return v.url.trim();
  }
  return "";
}

function getItemApplicableScenes(item) {
  if (Array.isArray(item?.applicableScenesList) && item.applicableScenesList.length) {
    return item.applicableScenesList.map((s) => String(s).trim().toUpperCase()).filter(Boolean);
  }
  const raw = item?.raw && typeof item.raw === "object" ? item.raw : item;
  return parseUserCouponApplicableScenes(raw);
}

/** 跳转按钮文案：开仓抵扣券 FIXED_AMOUNT 展示 Trade，其余沿用 Use */
export function getVoucherNavigateButtonLabelId(item) {
  return isFixedAmountCoupon(item) ? "activity.voucherTrade" : "activity.voucherUseAicopy";
}

/** 是否展示「使用/交易」：AVAILABLE 且（任一场景配置了 couponUrl，或可走分类兜底） */
export function canShowVoucherUse(item, showUse = true) {
  if (!showUse) return false;
  if (canShowVoucherCashRedeem(item)) return false;
  if (String(item?.status || "").toUpperCase() !== "AVAILABLE") return false;
  const raw = item?.raw && typeof item.raw === "object" ? item.raw : item;
  const couponUrl = resolveVoucherSceneCouponUrl(raw);
  if (couponUrlHasJumpTarget(couponUrl)) return true;
  return Boolean(resolveVoucherNavFallbackKind(item));
}

/** @deprecated 使用 canShowVoucherUse */
export const canShowVoucherAicopyUse = canShowVoucherUse;

function resolveVoucherNavFallbackKind(item) {
  if (isPercentOffCoupon(item)) return "LOSS";
  if (isFixedAmountCoupon(item)) return "DISCOUNT";
  if (getItemApplicableScenes(item).includes(COUPON_SCENE_AI_COPYTRADE)) return "AI_COPYTRADE";
  if (getItemApplicableScenes(item).includes("CFD")) return "DISCOUNT";
  return null;
}

/** 无 couponUrl 时 AI 跟单场景兜底 URL */
export function buildVoucherAicopyUseUrl() {
  return getUrl(getCouponNavFallbackPath("AI_COPYTRADE", { preferApp: false }));
}

export function navigateWebPath(rawPath, router) {
  const url = (rawPath || "").trim();
  if (!url) return false;
  const resolvedUrl = resolveAicopyDetailPathForViewport(url);
  if (/^https?:\/\//i.test(resolvedUrl)) {
    window.location.href = resolvedUrl;
    return true;
  }
  const path = resolvedUrl.startsWith("/") ? resolvedUrl : `/${resolvedUrl}`;
  const target = getUrl(path);
  if (router?.push) {
    router.push(target);
    return true;
  }
  window.location.href = target;
  return true;
}

/**
 * @param {{
 *   jumpUrls?: object|null,
 *   fallbackKind?: string|null,
 *   webFallbackPath?: string,
 *   router?: import('next/router').NextRouter,
 * }} options
 * @returns {boolean}
 */
export function navigateWithCouponJumpConfig({
  jumpUrls,
  fallbackKind,
  webFallbackPath,
  router,
} = {}) {
  const preferApp = shouldUseAppRouteForVoucherJump();

  if (jumpUrls) {
    if (preferApp) {
      const nativeRaw = resolveCouponUrlNativePath(jumpUrls);
      const appPath = resolveVoucherAppRoutePath(nativeRaw, fallbackKind);
      if (appPath) {
        Bridge.call("appRoute", { path: appPath, params: "" });
        return true;
      }
    } else {
      const webFromApi = resolveCouponUrlWebPath(jumpUrls);
      if (webFromApi && navigateWebPath(webFromApi, router)) {
        return true;
      }
    }
  }

  const fallbackPath =
    getCouponNavFallbackPath(fallbackKind, { preferApp }) ||
    (webFallbackPath || "").trim();
  if (!fallbackPath) return false;

  if (preferApp) {
    const appPath = resolveVoucherAppRoutePath(fallbackPath, fallbackKind);
    if (appPath) {
      Bridge.call("appRoute", { path: appPath, params: "" });
      return true;
    }
  }

  return navigateWebPath(fallbackPath, router);
}

/**
 * 券使用跳转：App 内有 ios/android 走 appRoute；Web 走 H5；均未配置则按券类型兜底
 * @param {object} item — normalizeUserCoupon 结果（含 raw、id）
 * @param {{ router?: import('next/router').NextRouter }} [options]
 */
export function navigateVoucherUse(item, { router } = {}) {
  const raw = item?.raw && typeof item.raw === "object" ? item.raw : item;
  const couponUrl = resolveVoucherSceneCouponUrl(raw);
  const fallbackKind = resolveVoucherNavFallbackKind(item);

  navigateWithCouponJumpConfig({
    jumpUrls: couponUrl,
    fallbackKind,
    router,
  });
}
